/**
 * Artwork Image Resolver Utility
 * Shared utility functions for resolving artwork image URLs and orientation
 */

/**
 * Resolve the best available image URL for an artwork
 * @param {Object} artwork - The artwork object
 * @param {Object} coreStore - The gallery core store
 * @returns {string} The resolved image URL or empty string if not found
 */
export function resolveArtworkImageUrl(artwork, coreStore) {
  let imageUrl = '';

  // Try to get URLs from core store first (highest priority)
  const storedUrls = coreStore?.getArtworkUrls?.(artwork);
  if (storedUrls?.zoom) {
    imageUrl = storedUrls.zoom;
  } else if (storedUrls?.large) {
    imageUrl = storedUrls.large;
  } else if (storedUrls?.display) {
    imageUrl = storedUrls.display;
  } else if (artwork.featured_image) {
    imageUrl = artwork.featured_image;
  } else if (artwork.image) {
    imageUrl = artwork.image;
  } else if (artwork.images && artwork.images.length > 0) {
    imageUrl = artwork.images[0].src || artwork.images[0];
  }

  if (imageUrl) {
    const [baseUrl] = imageUrl.split('?');
    imageUrl = baseUrl;

    if (imageUrl.startsWith('//')) {
      imageUrl = 'https:' + imageUrl;
    }
  }

  return imageUrl;
}

/**
 * Detect artwork orientation from various sources
 * @param {Object} artwork - The artwork object
 * @returns {'horizontal'|'vertical'} The detected orientation
 */
export function detectArtworkOrientation(artwork) {
  if (artwork.orientation) {
    return artwork.orientation;
  }

  if (artwork.image_width && artwork.image_height) {
    return artwork.image_width > artwork.image_height ? 'horizontal' : 'vertical';
  }

  if (artwork.tags) {
    // Handle tags as either string or array
    const tagsString = Array.isArray(artwork.tags)
      ? artwork.tags.join(' ').toLowerCase()
      : String(artwork.tags).toLowerCase();

    if (tagsString.includes('landscape') || tagsString.includes('horizontal')) {
      return 'horizontal';
    }
    if (tagsString.includes('portrait') || tagsString.includes('vertical')) {
      return 'vertical';
    }
  }

  return 'horizontal';
}